import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Plus, 
  AlertCircle,
  CheckSquare,
  Calendar
} from "lucide-react";
import { cn } from "@/lib/utils";

const STATUS_CONFIG = {
  "To Do": { header: "bg-gray-100", text: "text-gray-700", dot: "bg-gray-500" },
  "In Progress": { header: "bg-blue-100", text: "text-blue-700", dot: "bg-blue-500" },
  "In Review": { header: "bg-purple-100", text: "text-purple-700", dot: "bg-purple-500" },
  "Blocked": { header: "bg-red-100", text: "text-red-700", dot: "bg-red-500" },
  "Completed": { header: "bg-green-100", text: "text-green-700", dot: "bg-green-500" },
  "Pending Approval": { header: "bg-amber-100", text: "text-amber-700", dot: "bg-amber-500" }
};

const PRIORITY_CONFIG = {
  "Urgent": "bg-red-500",
  "High": "bg-orange-500",
  "Medium": "bg-amber-500",
  "Low": "bg-green-500"
};

const COLUMNS = ["To Do", "In Progress", "In Review", "Blocked", "Completed"];

const TaskBoardCard = ({ task, projects, teamMembers, onStatusChange, onClick, onComplete }) => {
  const project = projects?.find(p => p.id === task.projectId);
  const assignee = teamMembers?.find(m => m.id === task.assignedTo);
  
  const subtasks = task.subtasks || [];
  const completedSubtasks = subtasks.filter(st => st.completed).length;
  const isOverdue = (() => {
    if (!task.dueDate || task.status === "Completed") return false;
    let dueDate;
    try {
      if (typeof task.dueDate === 'string') {
        dueDate = new Date(task.dueDate);
      } else if (task.dueDate && typeof task.dueDate.toDate === 'function') {
        dueDate = task.dueDate.toDate();
      } else if (task.dueDate && typeof task.dueDate === 'object' && task.dueDate.seconds) {
        dueDate = new Date(task.dueDate.seconds * 1000);
      } else {
        dueDate = new Date(task.dueDate);
      }
      return dueDate < new Date();
    } catch {
      return false;
    }
  })();
  
  const formatDueDate = (date) => {
    if (!date) return "";
    let dueDate;
    try {
      if (typeof date === 'string') {
        dueDate = new Date(date);
      } else if (date && typeof date.toDate === 'function') {
        dueDate = date.toDate();
      } else if (date && typeof date === 'object') {
        dueDate = new Date(date.seconds * 1000);
      } else {
        dueDate = new Date(date);
      }
      if (isNaN(dueDate.getTime())) return "";
    } catch {
      return "";
    }
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const checkDate = new Date(dueDate);
    checkDate.setHours(0, 0, 0, 0);
    
    if (checkDate.toDateString() === today.toDateString()) return "Today";
    if (checkDate.toDateString() === tomorrow.toDateString()) return "Tomorrow";
    return dueDate.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const getInitials = (name) => {
    if (!name) return "U";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  const getAvatarColor = (name) => {
    const colors = ["from-blue-500", "from-purple-500", "from-pink-500", "from-green-500"];
    const index = name?.charCodeAt(0) || 0;
    return colors[index % colors.length];
  };

  const handleCheckboxChange = (e) => {
    e.stopPropagation();
    const newStatus = task.status === "Completed" ? "To Do" : "Completed";
    onComplete && onComplete(task.id, newStatus);
  };

  return (
    <Card 
      className="cursor-grab active:cursor-grabbing shadow-sm hover:shadow-md transition-all hover:-translate-y-0.5"
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData("taskId", task.id);
        e.dataTransfer.effectAllowed = "move";
      }}
      onClick={() => onClick && onClick(task)}
    >
      <CardContent className="p-3 space-y-2">
        <div className="flex items-start gap-2">
          <Checkbox 
            checked={task.status === "Completed"}
            onChange={handleCheckboxChange}
            className="mt-1 w-4 h-4"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="flex-1 min-w-0">
            <span className={cn(
              "font-semibold text-navy text-sm line-clamp-2",
              task.status === "Completed" && "line-through text-gray-400"
            )}>
              {task.title}
            </span>
          </div>
        </div>

        {project && (
          <div 
            className="text-xs px-2 py-0.5 rounded-full w-fit"
            style={{ backgroundColor: `${project.color}20`, color: project.color }}
          >
            {project.name}
          </div>
        )}

        <div className="flex items-center gap-2">
          <span className={cn("w-2 h-2 rounded-full", PRIORITY_CONFIG[task.priority] || PRIORITY_CONFIG["Medium"])} />
          <span className="text-xs text-gray-500">{task.priority || "Medium"}</span>
        </div>

        {subtasks.length > 0 && (
          <div className="flex items-center gap-2">
            <Progress value={(completedSubtasks / subtasks.length) * 100} className="flex-1 h-1.5" />
            <span className="text-xs text-gray-500">{completedSubtasks}/{subtasks.length}</span>
          </div>
        )}

        <div className="flex items-center justify-between">
          {assignee ? (
            <div 
              className={cn(
                "w-6 h-6 rounded-full bg-gradient-to-br flex items-center justify-center text-white text-xs",
                getAvatarColor(assignee.name)
              )}
              title={assignee.name}
            >
              {getInitials(assignee.name)}
            </div>
          ) : (
            <div className="w-6 h-6 rounded-full bg-slate-200" />
          )}
          
          {task.dueDate && (
            <div className={cn("text-xs flex items-center gap-1", isOverdue && "text-red-600 font-medium")}>
              {isOverdue && <AlertCircle className="w-3 h-3" />}
              <Calendar className="w-3 h-3" />
              {formatDueDate(task.dueDate)}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

const TaskBoardColumn = ({ status, tasks, projects, teamMembers, onDrop, onAddTask, onTaskClick, onComplete }) => {
  const statusConfig = STATUS_CONFIG[status] || STATUS_CONFIG["To Do"];
  
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const taskId = e.dataTransfer.getData("taskId");
    onDrop && onDrop(taskId, status);
  };

  return (
    <div 
      className="flex-shrink-0 w-72"
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <div className={cn("flex items-center justify-between p-3 rounded-t-lg", statusConfig.header)}>
        <div className="flex items-center gap-2">
          <span className={cn("w-2 h-2 rounded-full", statusConfig.dot)} />
          <span className={cn("font-semibold text-sm", statusConfig.text)}>{status}</span>
        </div>
        <Badge variant="secondary" className="text-xs">
          {tasks.length}
        </Badge>
      </div>
      
      <div className="bg-slate-50 p-2 rounded-b-lg min-h-[500px] space-y-2">
        {tasks.map(task => (
          <TaskBoardCard 
            key={task.id} 
            task={task}
            projects={projects}
            teamMembers={teamMembers}
            onClick={onTaskClick}
            onComplete={onComplete}
          />
        ))}
        <Button 
          variant="ghost" 
          className="w-full justify-start text-gray-500 hover:text-navy"
          onClick={() => onAddTask && onAddTask(status)}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Task
        </Button>
      </div>
    </div>
  );
};

const TaskBoard = ({ tasks, projects, teamMembers, onStatusChange, onTaskClick, onAddTask, onComplete }) => {
  const getTasksByStatus = (status) => {
    return tasks.filter(t => t.status === status);
  };

  return (
    <div className="flex gap-4 overflow-x-auto pb-4">
      {COLUMNS.map(status => (
        <TaskBoardColumn
          key={status}
          status={status}
          tasks={getTasksByStatus(status)}
          projects={projects}
          teamMembers={teamMembers}
          onDrop={onStatusChange}
          onAddTask={onAddTask}
          onTaskClick={onTaskClick}
          onComplete={onComplete}
        />
      ))}
    </div>
  );
};

export default TaskBoard;